import { List, ListSubheader, ListItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { AirCraftType } from 'shared/types';
import { TFunction } from 'i18next';
import React from 'react';
import { aircraftIconNameToDataMap } from 'features/mapping/components/layers/AircraftsLayer/utils';
import {
  DEFAULT_STROKE_COLOR,
  fillColorForVariant,
} from 'features/mapping/components/layers/AircraftsLayer/AircraftsLayer.styles';
import { UserStatusChip } from 'components/UserStatusChip';
import { LegendListItem } from './LegendListItem';
import { LegendSectionTitle } from './LegendSectionTitle';

const useStyles = makeStyles()((theme) => ({
  icon: {
    marginRight: theme.spacing(0),
    width: theme.spacing(4.5),
    minWidth: theme.spacing(4.5),
  },
  sectionHeader: {
    fontWeight: 'bold',
  },
  starIcon: {
    fontSize: 16,
    marginLeft: 4,
  },
}));

const buildLegendListItem = (
  t: TFunction,
  classification: AirCraftType,
): JSX.Element => {
  const icon = aircraftIconNameToDataMap[classification];
  const iconUri = icon.svg
    ? `data:image/svg+xml;base64,${window.btoa(
        ReactDOMServer.renderToStaticMarkup(
          React.cloneElement(aircraftIconNameToDataMap[classification].svg!, {
            fill: fillColorForVariant('default'),
            stroke: DEFAULT_STROKE_COLOR,
          }),
        ),
      )}`
    : icon.urls?.default;
  const titleKey = `map.legends.aircraft.legends.${classification}.title`;
  const subtitleKey = `map.legends.aircraft.legends.${classification}.subtitle`;
  return (
    <LegendListItem
      key={classification}
      iconSrc={iconUri}
      iconSize={30}
      primaryText={t(titleKey)}
      secondary={t(subtitleKey)}
    />
  );
};

const tankerSectionAircraft: Array<AirCraftType> = [
  'tankerType1Vlat',
  'tankerType1Lat',
  'tankerType2',
  'tankerType3',
  'tankerSeats',
  'tankerScooper',
];

const helicopterSectionAircraft: Array<AirCraftType> = [
  'heloType1Skycrane',
  'heloType1Chinook',
  'heloType1Blackhawk',
  'heloType2Medium',
  'heloType3Light',
];

const otherSectionAircraft: Array<AirCraftType> = [
  'fixedWing',
  'jumpShip',
  'drone',
];

export const AircraftLegend = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <List
      disablePadding
      dense
      subheader={
        <LegendSectionTitle endAdornment={<UserStatusChip type="members" />}>
          {t('map.legends.aircraft.title')}
        </LegendSectionTitle>
      }
    >
      <ListSubheader className={classes.sectionHeader}>
        {t('map.legends.aircraft.sections.airTanker.title')}
      </ListSubheader>
      <ListItem>
        {t('map.legends.aircraft.sections.airTanker.subtitle')}
      </ListItem>
      {tankerSectionAircraft.map((c: AirCraftType) =>
        buildLegendListItem(t, c),
      )}
      <ListSubheader className={classes.sectionHeader}>
        {t('map.legends.aircraft.sections.helicopter.title')}
      </ListSubheader>
      <ListItem>
        {t('map.legends.aircraft.sections.helicopter.subtitle')}
      </ListItem>
      {helicopterSectionAircraft.map((c: AirCraftType) =>
        buildLegendListItem(t, c),
      )}
      <ListSubheader className={classes.sectionHeader}>
        {t('map.legends.aircraft.sections.other.title')}
      </ListSubheader>
      {otherSectionAircraft.map((c: AirCraftType) => buildLegendListItem(t, c))}
    </List>
  );
};
